import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Layout, { DateInput, SearchableSelect } from './Layout';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate, getApiUrl, getTodayIso, parseDateToIso } from './config';
import { shareSaleInvoicePdf } from './saleInvoiceShare';

function Sale() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerVehicles, setCustomerVehicles] = useState([]);
    const [allVehicleOptions, setAllVehicleOptions] = useState([]);
    const [invoice, setInvoice] = useState({
        customer_id: '',
        date: getTodayIso(),
        total_amount: 0,
        paid_amount: 0,
        payment_mode: 'Cash',
        service_vehicle_no: '',
        service_vehicle_model: '',
        service_customer_mobile: '',
        service_km: '',
        job_card_no: ''
    });
    const [items, setItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ product_id: '', quantity: 1, price: 0, total: 0 });
    const [editingIndex, setEditingIndex] = useState(null);
    const [payments, setPayments] = useState([{ payment_mode: 'Cash', amount: 0 }]);
    const [customerName, setCustomerName] = useState('');
    const [newCustomer, setNewCustomer] = useState({ name: '', contact: '', address: '', email: '' });
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [prevBalance, setPrevBalance] = useState(0);
    const [saleShareSettings, setSaleShareSettings] = useState({
        whatsappText: true,
        pdf: false,
        pdfDays: 10
    });
    const [firmProfile, setFirmProfile] = useState({
        name: '',
        address: '',
        contact: '',
        email: '',
        gst_no: ''
    });
    const customerBalanceReqSeq = useRef(0);
    const latestCustomerIdRef = useRef("");

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get('edit');

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        if (!digits) return "";
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        const url = digits
            ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
            : `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const buildWhatsAppMessage = (invoiceId, selectedCustomer, prevBalanceOverride) => {
        const dateText = formatDate(invoice.date);
        const paymentLines = (payments || [])
            .map(p => ({ payment_mode: String(p?.payment_mode || "").trim(), amount: Number(p?.amount) || 0 }))
            .filter(p => p.payment_mode && p.amount > 0);
        const modeTotals = paymentLines.reduce((acc, p) => {
            const key = String(p.payment_mode || "").toLowerCase();
            if (key.includes("upi")) acc.upi += p.amount;
            else if (key.includes("bank") || key.includes("transfer")) acc.bank += p.amount;
            else acc.cash += p.amount;
            return acc;
        }, { cash: 0, upi: 0, bank: 0 });

        const itemLines = items.map((it, idx) => [
            `${idx + 1}. ${it.product_name || "-"}`,
            `Qty: ${Number(it.quantity || 0)}`,
            `Rate: ${Number(it.price || 0).toFixed(2)}`,
            `Amount: ${Number(it.total || 0).toFixed(2)}`
        ].join("\n"));

        const effectivePrevBalance = Number.isFinite(Number(prevBalanceOverride))
            ? Number(prevBalanceOverride)
            : (Number(prevBalance) || 0);
        const paidAmount = Number(invoice.paid_amount || 0);
        const totalAmount = Number(invoice.total_amount || 0);

        return [
            `Service Invoice #${invoiceId}`,
            ...(invoice.job_card_no ? [`Job Card No: ${invoice.job_card_no}`] : []),
            `Date: ${dateText}`,
            `Customer: ${selectedCustomer?.name || customerName || '-'}`,
            ...(invoice.service_customer_mobile ? [`Customer Mobile: ${invoice.service_customer_mobile}`] : []),
            `Vehicle No: ${invoice.service_vehicle_no || '-'}`,
            ...(invoice.service_vehicle_model ? [`Model: ${invoice.service_vehicle_model}`] : []),
            ...(invoice.service_km ? [`KM Reading: ${invoice.service_km}`] : []),
            ...(itemLines.length > 0 ? itemLines : ['-']),
            `Net Amount: ₹ ${totalAmount.toFixed(2)}`,
            `Previous Amt: ₹ ${effectivePrevBalance.toFixed(2)}`,
            `Total Amount: ₹ ${(effectivePrevBalance + totalAmount).toFixed(2)}`,
            `Cash Amt: ₹ ${Number(modeTotals.cash || 0).toFixed(2)}`,
            `UPI Amt: ₹ ${Number(modeTotals.upi || 0).toFixed(2)}`,
            `Bank Amt: ₹ ${Number(modeTotals.bank || 0).toFixed(2)}`,
            `Balance Amt: ₹ ${(effectivePrevBalance + totalAmount - paidAmount).toFixed(2)}`
        ].join("\n");
    };

    const handleInvoicePdfShare = async ({ invoiceId, jobCardNo, selectedCustomer, payload, previousBalance }) => {
        try {
            const invoiceForPdf = {
                ...payload,
                id: invoiceId,
                invoice_id: invoiceId,
                job_card_no: jobCardNo || payload.job_card_no || '',
                customer_name: selectedCustomer?.name || customerName || '',
                customer_contact: payload.service_customer_mobile || selectedCustomer?.contact || '',
                vehicle_no: payload.service_vehicle_no || '',
                items: items.map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    quantity: Number(item.quantity || 0),
                    price: Number(item.price || 0),
                    total: Number(item.total || 0)
                })),
                payments: payload.payments || []
            };
            const result = await shareSaleInvoicePdf({
                invoice: invoiceForPdf,
                customer: selectedCustomer,
                vehicle: {
                    vehicle_no: payload.service_vehicle_no || '',
                    vehicle_model: payload.service_vehicle_model || ''
                },
                previousBalance,
                firm: firmProfile
            });
            if (!result.shared && result.mode === 'download') {
                alert("Service Invoice PDF downloaded successfully.");
            }
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            const detail =
                err?.message ||
                "";
            const extra = status ? `Status: ${status}${detail ? `\nDetail: ${detail}` : ""}` : (detail ? `Detail: ${detail}` : "");
            alert(`Failed to prepare Service Invoice PDF.${extra ? `\n${extra}` : ""}`);
        }
    };

    const fetchPrevBalanceForInvoice = async (customerId, invoiceId, invoiceDate) => {
        const to = parseDateToIso(invoiceDate) || invoiceDate;
        const res = await axios.get(`${getApiUrl()}/reports/sales`, {
            params: { customer_id: customerId, to }
        });
        const rows = res.data || [];
        const hit = rows.find(r => String(r.invoice_id) === String(invoiceId));
        if (!hit) return 0;
        const cumulative = Number(hit.cumulative_balance ?? hit.net_balance ?? 0);
        const total = Number(hit.total_amount || 0);
        const paid = Number(hit.paid_amount || 0);
        return cumulative - (total - paid);
    };

    const fetchCustomerBalance = async (id) => {
        const requestedCustomerId = String(id || "");
        const seq = ++customerBalanceReqSeq.current;
        try {
            const res = await axios.get(`${getApiUrl()}/customers/${id}/balance`);
            if (seq !== customerBalanceReqSeq.current) return;
            if (requestedCustomerId && latestCustomerIdRef.current && requestedCustomerId !== latestCustomerIdRef.current) return;
            setPrevBalance(Number(res.data.balance) || 0);
        } catch (err) {
            console.error(err);
            if (seq !== customerBalanceReqSeq.current) return;
            if (requestedCustomerId && latestCustomerIdRef.current && requestedCustomerId !== latestCustomerIdRef.current) return;
            setPrevBalance(0);
        }
    };

    const fetchNextJobCard = async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/sales/next-job-card`);
            const nextJobCard = String(res?.data?.job_card_no || '').trim();
            if (!nextJobCard) return;
            setInvoice(prev => (prev.job_card_no === nextJobCard ? prev : { ...prev, job_card_no: nextJobCard }));
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCustomersList = async () => {
        const res = await axios.get(`${getApiUrl()}/customers`);
        setCustomers(res.data || []);
        return res.data || [];
    };

    const fetchCustomerVehicles = async (customerId, preferredVehicleNo = '', preferredVehicleModel = '') => {
        const normalizedCustomerId = String(customerId || '').trim();
        if (!normalizedCustomerId) {
            setCustomerVehicles([]);
            return [];
        }
        const res = await axios.get(`${getApiUrl()}/customers/${normalizedCustomerId}/vehicles`);
        const rows = Array.isArray(res.data) ? res.data : [];
        const normalizedVehicleNo = String(preferredVehicleNo || '').trim();
        const hasPreferred = normalizedVehicleNo && rows.some(v => String(v.vehicle_no || '').trim().toLowerCase() === normalizedVehicleNo.toLowerCase());
        const nextRows = hasPreferred || !normalizedVehicleNo
            ? rows
            : [{ id: `temp-${normalizedVehicleNo}`, customer_id: normalizedCustomerId, vehicle_no: normalizedVehicleNo, vehicle_model: preferredVehicleModel || '' }, ...rows];
        setCustomerVehicles(nextRows);
        return nextRows;
    };

    const fetchVehicleOptions = async (vehicleNo = '', customerId = '') => {
        const res = await axios.get(`${getApiUrl()}/customer-vehicles/search`, {
            params: {
                vehicle_no: String(vehicleNo || '').trim() || undefined,
                customer_id: String(customerId || '').trim() || undefined
            }
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        setAllVehicleOptions(rows);
        return rows;
    };

    const applyVehicleFromNumber = (vehicleNo) => {
        const normalizedVehicleNo = String(vehicleNo || '').trim().toLowerCase();
        if (!normalizedVehicleNo) return;
        const matchedVehicle =
            customerVehicles.find(v => String(v.vehicle_no || '').trim().toLowerCase() === normalizedVehicleNo) ||
            allVehicleOptions.find(v => String(v.vehicle_no || '').trim().toLowerCase() === normalizedVehicleNo);
        if (!matchedVehicle) return;

        if (String(matchedVehicle.customer_id || '') && String(matchedVehicle.customer_id || '') !== String(invoice.customer_id || '')) {
            const matchedCustomer = customers.find(c => String(c.id) === String(matchedVehicle.customer_id));
            setInvoice(prev => ({
                ...prev,
                customer_id: String(matchedVehicle.customer_id || ''),
                service_customer_mobile: matchedVehicle.customer_contact || matchedCustomer?.contact || '',
                service_vehicle_no: matchedVehicle.vehicle_no || prev.service_vehicle_no,
                service_vehicle_model: matchedVehicle.vehicle_model || '',
                service_km: prev.service_km
            }));
            setCustomerName(matchedVehicle.customer_name || matchedCustomer?.name || '');
            fetchCustomerVehicles(matchedVehicle.customer_id, matchedVehicle.vehicle_no || '', matchedVehicle.vehicle_model || '').catch(console.error);
            fetchCustomerBalance(matchedVehicle.customer_id).catch(console.error);
            return;
        }

        setInvoice(prev => ({
            ...prev,
            service_vehicle_no: matchedVehicle.vehicle_no || prev.service_vehicle_no,
            service_vehicle_model: matchedVehicle.vehicle_model || '',
            service_customer_mobile: prev.service_customer_mobile || matchedVehicle.customer_contact || prev.service_customer_mobile
        }));
    };

    const handleCustomerChange = async (nextId, opt, preferredVehicleNo = '', preferredVehicleModel = '') => {
        const selected = customers.find(c => String(c.id) === String(nextId));
        setInvoice(prev => ({
            ...prev,
            customer_id: nextId,
            service_customer_mobile: selected?.contact || '',
            service_vehicle_no: preferredVehicleNo || '',
            service_vehicle_model: preferredVehicleModel || ''
        }));
        setCustomerName(opt?.label || selected?.name || '');
        try {
            if (nextId) {
                await fetchCustomerVehicles(nextId, preferredVehicleNo, preferredVehicleModel);
            } else {
                setCustomerVehicles([]);
                await fetchVehicleOptions();
            }
        } catch (err) {
            console.error(err);
            setCustomerVehicles([]);
        }
    };

    const handleSaveNewCustomer = async () => {
        const payload = {
            name: String(newCustomer.name || '').trim(),
            contact: String(newCustomer.contact || '').trim(),
            address: String(newCustomer.address || '').trim(),
            email: String(newCustomer.email || '').trim()
        };
        if (!payload.name) {
            alert('Customer name is required');
            return;
        }
        try {
            const res = await axios.post(`${getApiUrl()}/customers`, payload);
            const newCustomerId = String(res?.data?.id || '').trim();
            const nextCustomers = await fetchCustomersList();
            const selected = nextCustomers.find(c => String(c.id) === newCustomerId);
            await handleCustomerChange(newCustomerId, selected ? { label: selected.name || '' } : null);
            setNewCustomer({ name: '', contact: '', address: '', email: '' });
            setShowCustomerModal(false);
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                'Failed to add customer';
            alert(message);
        }
    };

    useEffect(() => {
        const run = async () => {
            try {
                const [prodRes] = await Promise.all([
                    axios.get(`${getApiUrl()}/products`),
                    fetchCustomersList()
                ]);
                setProducts(prodRes.data || []);
                await fetchVehicleOptions();

                try {
                    const settingsRes = await axios.get(`${getApiUrl()}/firm-settings`);
                    const s = settingsRes?.data || {};
                    setFirmProfile({
                        name: s.name || '',
                        address: s.address || '',
                        contact: s.contact || '',
                        email: s.email || '',
                        gst_no: s.gst_no || ''
                    });
                    setSaleShareSettings({
                        whatsappText: Number(s.sale_share_whatsapp_text) ? true : false,
                        pdf: Number(s.sale_share_pdf) ? true : false,
                        pdfDays: Math.max(1, Math.min(60, Number(s.sale_share_pdf_days) || 10))
                    });
                } catch (e) {
                    console.error(e);
                }

                if (editId) {
                    if (!isAdmin) {
                        alert("Only admin can update records");
                        navigate("/sale");
                        return;
                    }
                    const res = await axios.get(`${getApiUrl()}/sales/${editId}`);
                    const data = res.data || {};
                    const invoiceDate = String(data.date || "").slice(0, 10) || getTodayIso();
                    setInvoice({
                        customer_id: data.customer_id || '',
                        date: invoiceDate,
                        total_amount: Number(data.total_amount || 0),
                        paid_amount: Number(data.paid_amount || 0),
                        payment_mode: data.payment_mode || 'Cash',
                        service_vehicle_no: data.service_vehicle_no || data.vehicle_no || '',
                        service_vehicle_model: data.service_vehicle_model || '',
                        service_customer_mobile: data.service_customer_mobile || data.customer_contact || '',
                        service_km: data.service_km || '',
                        job_card_no: data.job_card_no || ''
                    });
                    await fetchCustomerVehicles(data.customer_id, data.service_vehicle_no || data.vehicle_no || '', data.service_vehicle_model || '');
                    const prevFromApi = Number(data.prev_balance);
                    if (Number.isFinite(prevFromApi)) {
                        setPrevBalance(prevFromApi);
                    } else {
                        try {
                            const prev = await fetchPrevBalanceForInvoice(data.customer_id, editId, invoiceDate);
                            setPrevBalance(prev);
                        } catch (e) {
                            console.error(e);
                            try {
                                await fetchCustomerBalance(data.customer_id);
                            } catch {
                                setPrevBalance(0);
                            }
                        }
                    }
                    if (Array.isArray(data.payments) && data.payments.length > 0) {
                        setPayments(data.payments.map(p => ({ payment_mode: p.payment_mode || 'Cash', amount: p.amount || 0 })));
                    } else {
                        setPayments([{ payment_mode: data.payment_mode || 'Cash', amount: data.paid_amount || 0 }]);
                    }
                    const nextItems = Array.isArray(data.items) ? data.items.map(item => ({
                        product_id: item.product_id,
                        product_name: item.product_name,
                        quantity: Number(item.quantity) || 0,
                        price: Number(item.price) || 0,
                        total: Number(item.total) || 0
                    })) : [];
                    setItems(nextItems);
                } else {
                    fetchNextJobCard();
                }
            } catch (err) {
                console.error(err);
            }
        };
        run();
    }, [editId, isAdmin, navigate]);

    useEffect(() => {
        if (invoice.customer_id && !editId) {
            latestCustomerIdRef.current = String(invoice.customer_id || "");
            fetchCustomerBalance(invoice.customer_id);
        } else if (!invoice.customer_id) {
            latestCustomerIdRef.current = "";
            setPrevBalance(0);
            setCustomerVehicles([]);
        }
    }, [invoice.customer_id, editId]);

    useEffect(() => {
        if (!invoice.customer_id) {
            if (customerName !== '') setCustomerName('');
            return;
        }
        const customer = customers.find(c => String(c.id) === String(invoice.customer_id));
        const nextName = customer ? (customer.name || '') : '';
        if (customerName !== nextName) setCustomerName(nextName);
    }, [invoice.customer_id, customers, customerName]);

    useEffect(() => {
        const total = Number(currentItem.quantity || 0) * Number(currentItem.price || 0);
        setCurrentItem(prev => ({
            ...prev,
            total: Number(total.toFixed(2))
        }));
    }, [currentItem.quantity, currentItem.price]);

    useEffect(() => {
        const netTotal = Number(items.reduce((sum, item) => sum + (Number(item.total) || 0), 0).toFixed(2));
        setInvoice(prev => (prev.total_amount === netTotal ? prev : { ...prev, total_amount: netTotal }));
    }, [items]);

    useEffect(() => {
        const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        setInvoice(prev => (prev.paid_amount === totalPaid ? prev : { ...prev, paid_amount: totalPaid }));
    }, [payments]);

    const addItem = () => {
        const quantity = Number(currentItem.quantity || 0);
        if (!currentItem.product_id || quantity <= 0) return;
        const product = products.find(p => String(p.id) === String(currentItem.product_id));
        const newItem = {
            ...currentItem,
            quantity,
            price: Number(currentItem.price || 0),
            total: Number(currentItem.total || 0),
            product_name: product?.name || ""
        };
        setItems(prev => {
            if (editingIndex === null) return [...prev, newItem];
            return prev.map((it, idx) => (idx === editingIndex ? newItem : it));
        });
        setEditingIndex(null);
        setCurrentItem({ product_id: '', quantity: 1, price: 0, total: 0 });
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
        if (editingIndex === index) {
            setEditingIndex(null);
            setCurrentItem({ product_id: '', quantity: 1, price: 0, total: 0 });
        }
    };

    const startEditItem = (index) => {
        const it = items[index];
        if (!it) return;
        setEditingIndex(index);
        setCurrentItem({
            product_id: it.product_id,
            quantity: Number(it.quantity) || 1,
            price: Number(it.price) || 0,
            total: Number(it.total) || 0
        });
    };

    const handleSale = async (e) => {
        e.preventDefault();
        if (!invoice.customer_id) {
            alert("Select a valid customer");
            return;
        }
        if (!String(invoice.service_vehicle_no || "").trim()) {
            alert("Enter vehicle number");
            return;
        }
        if (items.length === 0) {
            alert("Add at least one item");
            return;
        }

        const selectedCustomer = customers.find(c => String(c.id) === String(invoice.customer_id));
        const payload = {
            ...invoice,
            date: parseDateToIso(invoice.date) || invoice.date,
            service_vehicle_no: String(invoice.service_vehicle_no || "").trim(),
            service_vehicle_model: String(invoice.service_vehicle_model || "").trim(),
            service_customer_mobile: String(invoice.service_customer_mobile || "").trim(),
            service_km: Number(invoice.service_km) > 0 ? Number(invoice.service_km) : null,
            job_card_no: String(invoice.job_card_no || "").trim(),
            paid_amount: parseFloat(invoice.paid_amount) || 0,
            payments: payments
                .map(p => ({ payment_mode: p.payment_mode, amount: parseFloat(p.amount) || 0 }))
                .filter(p => p.payment_mode && p.amount > 0),
            items: items.map(item => ({
                product_id: item.product_id,
                quantity: Number(item.quantity) || 0,
                price: Number(item.price) || 0,
                total: Number(item.total) || 0
            }))
        };

        try {
            if (editId) {
                if (!isAdmin) return alert("Only admin can update records");
                const updateRes = await axios.put(`${getApiUrl()}/sales/${editId}`, payload);
                const finalJobCardNo = String(updateRes?.data?.job_card_no || payload.job_card_no || '').trim();
                alert('Service Invoice Updated');
                if (saleShareSettings.pdf || window.confirm("Create Service Invoice PDF?")) {
                    await handleInvoicePdfShare({
                        invoiceId: editId,
                        jobCardNo: finalJobCardNo,
                        selectedCustomer,
                        payload,
                        previousBalance: prevBalance
                    });
                }
                if (saleShareSettings.whatsappText) {
                    const ok = window.confirm("Send WhatsApp text message?");
                    if (ok) {
                        const message = buildWhatsAppMessage(editId, selectedCustomer, prevBalance);
                        openWhatsApp(payload.service_customer_mobile || selectedCustomer?.contact, message);
                    }
                }
                navigate('/reports/sale');
            } else {
                const res = await axios.post(`${getApiUrl()}/sales`, payload);
                const newId = res?.data?.id || "";
                const savedJobCardNo = String(res?.data?.job_card_no || payload.job_card_no || '').trim();
                alert('Service Invoice Recorded');
                if (saleShareSettings.pdf || window.confirm("Create Service Invoice PDF?")) {
                    await handleInvoicePdfShare({
                        invoiceId: newId,
                        jobCardNo: savedJobCardNo,
                        selectedCustomer,
                        payload,
                        previousBalance: prevBalance
                    });
                }
                if (saleShareSettings.whatsappText) {
                    const ok = window.confirm("Send WhatsApp text message?");
                    if (ok) {
                        const message = buildWhatsAppMessage(newId, selectedCustomer, prevBalance);
                        openWhatsApp(payload.service_customer_mobile || selectedCustomer?.contact, message);
                    }
                }
                setItems([]);
                setInvoice({
                    customer_id: '',
                    date: getTodayIso(),
                    total_amount: 0,
                    paid_amount: 0,
                    payment_mode: 'Cash',
                    service_vehicle_no: '',
                    service_vehicle_model: '',
                    service_customer_mobile: '',
                    service_km: '',
                    job_card_no: savedJobCardNo
                });
                setCustomerVehicles([]);
                setCustomerName('');
                setPayments([{ payment_mode: 'Cash', amount: 0 }]);
                setCurrentItem({ product_id: '', quantity: 1, price: 0, total: 0 });
                fetchNextJobCard();
            }
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to save service invoice";
            alert(message);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>{editId ? `Edit Service Invoice #${editId}` : 'New Service Invoice'}</h2>

                <div className="card p-4 mb-4 shadow-sm border-0">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Job Card No</label>
                            <input
                                type="text"
                                className="form-control"
                                value={invoice.job_card_no}
                                readOnly
                                placeholder="Auto generated"
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Date</label>
                            <DateInput value={invoice.date} onChange={(v) => setInvoice(prev => ({ ...prev, date: v }))} required />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Customer</label>
                            <SearchableSelect
                                value={invoice.customer_id}
                                options={customers.map(c => ({ value: String(c.id), label: c.name || '' }))}
                                placeholder="Type customer name..."
                                required
                                onChange={(nextId, opt) => { handleCustomerChange(nextId, opt); }}
                                allowClear
                            />
                        </div>
                        <div className="col-md-2 d-grid">
                            <label className="form-label fw-bold">&nbsp;</label>
                            <button type="button" className="btn btn-outline-primary" onClick={() => setShowCustomerModal(true)}>
                                Add Customer
                            </button>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Customer Mobile</label>
                            <input
                                type="text"
                                className="form-control"
                                value={invoice.service_customer_mobile}
                                onChange={e => setInvoice(prev => ({ ...prev, service_customer_mobile: e.target.value }))}
                                placeholder="Customer mobile no"
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Vehicle No</label>
                            <input
                                type="text"
                                className="form-control"
                                list="customer-vehicle-nos"
                                value={invoice.service_vehicle_no}
                                onChange={e => {
                                    const nextVehicleNo = e.target.value;
                                    setInvoice(prev => ({ ...prev, service_vehicle_no: nextVehicleNo }));
                                    fetchVehicleOptions(nextVehicleNo).catch(console.error);
                                    applyVehicleFromNumber(nextVehicleNo);
                                }}
                                onBlur={(e) => applyVehicleFromNumber(e.target.value)}
                                placeholder="MH12AB1234"
                            />
                            <datalist id="customer-vehicle-nos">
                                {(invoice.customer_id ? customerVehicles : allVehicleOptions).map(v => (
                                    <option key={v.id} value={v.vehicle_no}>
                                        {(v.customer_name || v.customer_contact)
                                            ? `${v.vehicle_no} - ${v.customer_name || ''}${v.vehicle_model ? ` - ${v.vehicle_model}` : ''}`
                                            : (v.vehicle_model ? `${v.vehicle_no} - ${v.vehicle_model}` : v.vehicle_no)}
                                    </option>
                                ))}
                            </datalist>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Model</label>
                            <input
                                type="text"
                                className="form-control"
                                value={invoice.service_vehicle_model}
                                onChange={e => setInvoice(prev => ({ ...prev, service_vehicle_model: e.target.value }))}
                                placeholder="Vehicle model"
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Km Reading</label>
                            <input
                                type="number"
                                className="form-control"
                                value={invoice.service_km}
                                onChange={e => setInvoice(prev => ({ ...prev, service_km: e.target.value }))}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                    </div>
                </div>

                <div className="card p-4 mb-4 bg-light">
                    <h5>Add Items</h5>
                    <div className="row g-3">
                        <div className="col-md-5">
                            <label className="form-label">Item / Service</label>
                            <select
                                className="form-select"
                                value={currentItem.product_id}
                                onChange={e => {
                                    const prod = products.find(p => String(p.id) === String(e.target.value));
                                    setCurrentItem(prev => ({
                                        ...prev,
                                        product_id: e.target.value,
                                        price: Number(prod?.price || 0)
                                    }));
                                }}
                            >
                                <option value="">Select Item</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Qty</label>
                            <input
                                type="number"
                                className="form-control"
                                value={currentItem.quantity}
                                onChange={e => setCurrentItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Rate</label>
                            <input
                                type="number"
                                className="form-control"
                                value={currentItem.price}
                                onChange={e => setCurrentItem(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Total</label>
                            <input type="number" className="form-control" value={currentItem.total} readOnly />
                        </div>
                        <div className="col-12 d-grid d-md-flex justify-content-end">
                            <button type="button" className="btn btn-primary" onClick={addItem}>
                                {editingIndex !== null ? "Update Item" : "Add Item"}
                            </button>
                            {editingIndex !== null && (
                                <button
                                    type="button"
                                    className="btn btn-secondary ms-md-2 mt-2 mt-md-0"
                                    onClick={() => {
                                        setEditingIndex(null);
                                        setCurrentItem({ product_id: '', quantity: 1, price: 0, total: 0 });
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="table-responsive mb-4">
                    <table className="table table-bordered">
                        <thead className="table-dark">
                            <tr>
                                <th>Item / Service</th>
                                <th>Qty</th>
                                <th>Rate</th>
                                <th>Total</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.product_name}</td>
                                    <td>{Number(item.quantity || 0)}</td>
                                    <td>{Number(item.price || 0).toFixed(2)}</td>
                                    <td>{Number(item.total || 0).toFixed(2)}</td>
                                    <td>
                                        <button className="btn btn-warning btn-sm me-1" type="button" onClick={() => startEditItem(index)}>Edit</button>
                                        <button className="btn btn-danger btn-sm" type="button" onClick={() => removeItem(index)}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colSpan="3" className="text-end">Invoice Total</th>
                                <th colSpan="2">₹ {Number(invoice.total_amount || 0).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end">Previous Balance</th>
                                <th colSpan="2">₹ {Number(prevBalance || 0).toFixed(2)}</th>
                            </tr>
                            <tr className="table-secondary">
                                <th colSpan="3" className="text-end fw-bold">Grand Total</th>
                                <th colSpan="2" className="fw-bold">₹ {(Number(prevBalance || 0) + Number(invoice.total_amount || 0)).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end text-success align-middle">Amount Paid</th>
                                <th colSpan="2">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Mode</th>
                                                    <th>Amount</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments.map((p, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={p.payment_mode}
                                                                onChange={e => {
                                                                    const next = [...payments];
                                                                    next[idx] = { ...next[idx], payment_mode: e.target.value };
                                                                    setPayments(next);
                                                                }}
                                                            >
                                                                <option value="Cash">Cash</option>
                                                                <option value="UPI">UPI</option>
                                                                <option value="Bank Transfer">Bank Transfer</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={p.amount}
                                                                onChange={e => {
                                                                    const next = [...payments];
                                                                    next[idx] = { ...next[idx], amount: e.target.value };
                                                                    setPayments(next);
                                                                }}
                                                                onFocus={(e) => e.target.select()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => setPayments(payments.filter((_, i) => i !== idx))}
                                                                disabled={payments.length === 1}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm mt-2"
                                        onClick={() => setPayments([...payments, { payment_mode: 'Cash', amount: 0 }])}
                                    >
                                        Add Mode
                                    </button>
                                </th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end text-danger fw-bold">Remaining Balance</th>
                                <th colSpan="2" className="fw-bold">₹ {(Number(prevBalance || 0) + Number(invoice.total_amount || 0) - Number(invoice.paid_amount || 0)).toFixed(2)}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="d-flex gap-2">
                    <button className="btn btn-success" onClick={handleSale}>
                        {editId ? 'Update Service Invoice' : 'Save Service Invoice'}
                    </button>
                    <button className="btn btn-secondary" type="button" onClick={() => navigate('/reports/sale')}>
                        Back To Report
                    </button>
                </div>

                {showCustomerModal ? (
                    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add Customer</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowCustomerModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Customer Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newCustomer.name}
                                                onChange={e => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Mobile</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newCustomer.contact}
                                                onChange={e => setNewCustomer(prev => ({ ...prev, contact: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Email</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newCustomer.email}
                                                onChange={e => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Address</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newCustomer.address}
                                                onChange={e => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowCustomerModal(false);
                                            setNewCustomer({ name: '', contact: '', address: '', email: '' });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleSaveNewCustomer}>
                                        Save Customer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </Layout>
    );
}

export default Sale;
